import { X, DollarSign } from "lucide-react";
import { formatFullCurrency } from "@/utils/helpers";

const SavingsDetailModal = ({ 
  savingsDetailData, 
  savingsDetailLoading, 
  selectedYear, 
  setSelectedYear, 
  selectedPeriod, 
  setSelectedPeriod, 
  onClose 
}) => {
  const formatCurrency = formatFullCurrency;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col border border-slate-700">
        {/* Header */}
        <div className="flex items-center justify-between p-5 bg-emerald-950/50 border-b border-emerald-900/50">
          <div>
            <h2 className="text-xl font-bold text-white font-['Manrope']">
              Savings Detail
            </h2>
            {savingsDetailData && (
              <p className="text-sm text-emerald-400 mt-1">
                {savingsDetailData.payrule} - {savingsDetailData.state_name}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {savingsDetailLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
            </div>
          ) : savingsDetailData ? (
            <div className="space-y-6">
              {/* Calculation Formula Explanation */}
              <div className="p-4 rounded-xl bg-slate-800 border border-slate-700">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-lg bg-amber-900/50">
                    <DollarSign className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white">
                      {savingsDetailData.over_compliance_type === 'meal_break' ? 'Meal Break' :
                       savingsDetailData.over_compliance_type === 'double_time' ? 'Double Time' : 'Overtime'} Over-Compliance
                    </p>
                  </div>
                </div>
                {/* Formula Box */}
                <div className="p-3 rounded-lg bg-slate-900 border border-slate-600">
                  <p className="text-xs text-slate-400 uppercase tracking-wide mb-2">Savings Formula</p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="px-2 py-1 rounded bg-blue-900/50 text-blue-300 text-sm font-mono">Hourly Rate</span>
                    <span className="text-slate-500">x</span>
                    <span className="px-2 py-1 rounded bg-amber-900/50 text-amber-300 text-sm font-mono">{savingsDetailData.multiplier}</span>
                    <span className="text-slate-500">x</span>
                    <span className="px-2 py-1 rounded bg-emerald-900/50 text-emerald-300 text-sm font-mono">{savingsDetailData.hour_type} Hours</span>
                    <span className="text-slate-500">=</span>
                    <span className="px-2 py-1 rounded bg-emerald-600 text-white text-sm font-semibold">Overpayment</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    {savingsDetailData.over_compliance_type === 'meal_break' 
                      ? 'Meal break pay (1.0x) is being paid when not legally required.'
                      : savingsDetailData.over_compliance_type === 'double_time'
                      ? 'Double time (2.0x) includes 1.0x extra premium above regular pay.'
                      : 'Overtime (1.5x) includes 0.5x extra premium above regular pay.'}
                  </p>
                </div>
              </div>

              {/* Year Comparison */}
              <div className="grid md:grid-cols-2 gap-4">
                {/* 2025 */}
                <div className="p-4 rounded-xl bg-slate-800 border border-slate-700">
                  <h4 className="text-sm font-semibold text-slate-400 mb-3">2025 (Would Have Saved)</h4>
                  <div className="space-y-3">
                    <div>
                      <p className="text-3xl font-bold text-emerald-400">
                        {formatCurrency(savingsDetailData.summary.last_year_2025.total_savings)}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-slate-500">If config was fixed</span>
                        <span className="px-2 py-0.5 rounded-full bg-emerald-900/50 text-emerald-300 text-xs font-semibold">
                          {savingsDetailData.summary.last_year_2025.savings_percentage?.toFixed(2)}% of wages
                        </span>
                      </div>
                    </div>
                    <div className="p-2 rounded-lg bg-slate-900/50 text-xs text-slate-500">
                      Total wages: {formatCurrency(savingsDetailData.summary.last_year_2025.total_wages || 0)}
                    </div>
                    <div className="grid grid-cols-3 gap-2 pt-3 border-t border-slate-700">
                      <div>
                        <p className="text-lg font-semibold text-white">
                          {formatCurrency(savingsDetailData.summary.last_year_2025.monthly)}
                        </p>
                        <p className="text-xs text-slate-500">Monthly</p>
                      </div>
                      <div>
                        <p className="text-lg font-semibold text-white">
                          {formatCurrency(savingsDetailData.summary.last_year_2025.quarterly)}
                        </p>
                        <p className="text-xs text-slate-500">Quarterly</p>
                      </div>
                      <div>
                        <p className="text-lg font-semibold text-white">
                          {savingsDetailData.summary.last_year_2025.employee_count}
                        </p>
                        <p className="text-xs text-slate-500">Employees</p>
                      </div>
                    </div>
                    <div className="text-xs text-slate-400">
                      {savingsDetailData.summary.last_year_2025.total_hours.toLocaleString()} total {savingsDetailData.hour_type.toLowerCase()} hours
                    </div>
                  </div>
                </div>

                {/* 2026 */}
                <div className="p-4 rounded-xl bg-slate-800 border border-emerald-700/50">
                  <h4 className="text-sm font-semibold text-emerald-400 mb-3">2026 (Projected Savings)</h4>
                  <div className="space-y-3">
                    <div>
                      <p className="text-3xl font-bold text-emerald-400">
                        {formatCurrency(savingsDetailData.summary.projected_2026.total_savings)}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-slate-500">After config change</span>
                        <span className="px-2 py-0.5 rounded-full bg-emerald-900/50 text-emerald-300 text-xs font-semibold">
                          {savingsDetailData.summary.projected_2026.savings_percentage?.toFixed(2)}% of wages
                        </span>
                      </div>
                    </div>
                    <div className="p-2 rounded-lg bg-slate-900/50 text-xs text-slate-500">
                      Total wages: {formatCurrency(savingsDetailData.summary.projected_2026.total_wages || 0)}
                    </div>
                    <div className="grid grid-cols-3 gap-2 pt-3 border-t border-slate-700">
                      <div>
                        <p className="text-lg font-semibold text-white">
                          {formatCurrency(savingsDetailData.summary.projected_2026.monthly)}
                        </p>
                        <p className="text-xs text-slate-500">Monthly</p>
                      </div>
                      <div>
                        <p className="text-lg font-semibold text-white">
                          {formatCurrency(savingsDetailData.summary.projected_2026.quarterly)}
                        </p>
                        <p className="text-xs text-slate-500">Quarterly</p>
                      </div>
                      <div>
                        <p className="text-lg font-semibold text-white">
                          {savingsDetailData.summary.projected_2026.employee_count}
                        </p>
                        <p className="text-xs text-slate-500">Employees</p>
                      </div>
                    </div>
                    <div className="text-xs text-slate-400">
                      {savingsDetailData.summary.projected_2026.total_hours.toLocaleString()} total {savingsDetailData.hour_type.toLowerCase()} hours
                    </div>
                  </div>
                </div>
              </div>

              {/* Over-Compliance Reason */}
              {savingsDetailData.over_compliance_reason && (
                <div className="p-4 rounded-lg bg-amber-950/30 border border-amber-800/50">
                  <p className="text-xs font-semibold text-amber-400 uppercase tracking-wide mb-2">Why Over-Compliant</p>
                  <p className="text-sm text-slate-300">{savingsDetailData.over_compliance_reason}</p>
                </div>
              )}

              {/* Employee Breakdown Table with Calculation */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-white">Employee Savings Breakdown ({selectedYear} - {selectedPeriod})</h4>
                  <div className="flex gap-2">
                    <div className="flex gap-1">
                      <button
                        onClick={() => setSelectedYear('2025')}
                        className={`px-2 py-1 text-xs rounded-md font-medium transition-colors ${
                          selectedYear === '2025'
                            ? 'bg-slate-600 text-white'
                            : 'bg-slate-700/50 text-slate-400 hover:bg-slate-700'
                        }`}
                      >
                        2025
                      </button>
                      <button
                        onClick={() => setSelectedYear('2026')}
                        className={`px-2 py-1 text-xs rounded-md font-medium transition-colors ${
                          selectedYear === '2026'
                            ? 'bg-emerald-600 text-white'
                            : 'bg-slate-700/50 text-slate-400 hover:bg-slate-700'
                        }`}
                      >
                        2026
                      </button>
                    </div>
                    <div className="flex gap-1">
                      {['monthly', 'quarterly', 'annual'].map((period) => (
                        <button
                          key={period}
                          onClick={() => setSelectedPeriod(period)}
                          className={`px-2 py-1 text-xs rounded-md font-medium transition-colors ${
                            selectedPeriod === period
                              ? 'bg-emerald-600 text-white'
                              : 'bg-slate-700/50 text-slate-400 hover:bg-slate-700'
                          }`}
                        >
                          {period.charAt(0).toUpperCase() + period.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="rounded-lg border border-slate-700 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-800">
                        <th className="text-left p-3 text-slate-400 font-medium">Employee ID</th>
                        <th className="text-left p-3 text-slate-400 font-medium">State</th>
                        <th className="text-right p-3 text-slate-400 font-medium">Hourly Rate</th>
                        <th className="text-center p-3 text-slate-400 font-medium">x</th>
                        <th className="text-right p-3 text-slate-400 font-medium">Multiplier</th>
                        <th className="text-center p-3 text-slate-400 font-medium">x</th>
                        <th className="text-right p-3 text-slate-400 font-medium">{savingsDetailData.hour_type} Hrs</th>
                        <th className="text-center p-3 text-slate-400 font-medium">=</th>
                        <th className="text-right p-3 text-slate-400 font-medium">Overpayment</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedYear === '2025' ? savingsDetailData.employees_2025 : savingsDetailData.employees_2026).slice(0, 10).map((emp, idx) => (
                        <tr key={emp.employee_id} className={`border-t border-slate-700 ${idx % 2 === 0 ? 'bg-slate-900/50' : ''}`}>
                          <td className="p-3 text-white font-mono text-xs">{emp.employee_id}</td>
                          <td className="p-3 text-slate-300">{emp.state}</td>
                          <td className="p-3 text-right text-blue-300 font-mono">${emp.hourly_rate.toFixed(2)}</td>
                          <td className="p-3 text-center text-slate-500">x</td>
                          <td className="p-3 text-right text-amber-300 font-mono">{savingsDetailData.multiplier}</td>
                          <td className="p-3 text-center text-slate-500">x</td>
                          <td className="p-3 text-right text-emerald-300 font-mono">
                            {(emp[`hours_${selectedPeriod}`] || 0).toFixed(1)}
                          </td>
                          <td className="p-3 text-center text-slate-500">=</td>
                          <td className="p-3 text-right text-emerald-400 font-semibold">
                            {formatCurrency(emp[`savings_${selectedPeriod}`] || 0)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-slate-800 border-t-2 border-emerald-700">
                        <td colSpan="8" className="p-3 text-right text-sm font-semibold text-white">Total Overpayment:</td>
                        <td className="p-3 text-right text-emerald-400 font-bold text-lg">
                          {formatCurrency(
                            selectedYear === '2025' 
                              ? savingsDetailData.summary.last_year_2025[selectedPeriod]
                              : savingsDetailData.summary.projected_2026[selectedPeriod]
                          )}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                  {(selectedYear === '2025' ? savingsDetailData.employees_2025 : savingsDetailData.employees_2026).length > 10 && (
                    <div className="p-3 bg-slate-800 text-center text-xs text-slate-400">
                      Showing top 10 of {(selectedYear === '2025' ? savingsDetailData.employees_2025 : savingsDetailData.employees_2026).length} employees
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <p className="text-slate-400 text-center">Failed to load savings detail</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default SavingsDetailModal;
